import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';
import { isStandalone, detectPlatform, shouldShowInstallNudge, dismissInstallTrigger, recordInstallTrigger } from '@/lib/deviceDetect';
import { isInstalled } from '@/lib/installManager';
import { useAuth } from '@/lib/auth';
import { track } from '@/lib/analytics';
import { InstallWalkthrough } from './InstallWalkthrough';
import { onGlobalMatch, type MatchNotification } from '@/hooks/useWebSocket';

// Routes where this full-screen match-install bottom sheet must never appear.
// Mirrors the SUPPRESSED_PATHS list used by PushDeclinedBanner / IOSInstallBanner:
// signup, email verification, onboarding, the VIP offer and other auth/full-screen
// flows must not be interrupted by a websocket-triggered install nag. Root "/"
// and marketing/landing routes are intentionally NOT listed.
const SUPPRESSED_PATHS = [
  '/login',
  '/register',
  '/verify-email',
  '/onboarding',
  '/auth',
  '/forgot-password',
  '/reset-password',
  '/install-prompt',
  '/push-prompt',
  '/vip-offer',
  '/install-app',
  '/subscription',
  '/delete-account',
  '/legal',
  '/privacy',
  '/terms',
];

const MATCH_NUDGE_STATE_PREFIX = 'pwa_match_nudge_state';
const ONBOARDING_INSTALL_SHOWN_KEY = 'pwa_install_shown';
const TRIGGER_ID = 'first_match';

const MATCH_COOLDOWN = 3;
const MAX_TOTAL_SHOWS = 3;

interface MatchNudgeState {
  showCount: number;
  matchesSinceLastShow: number;
}

function getNudgeStateKey(userId: number | undefined): string {
  return userId ? `${MATCH_NUDGE_STATE_PREFIX}_${userId}` : MATCH_NUDGE_STATE_PREFIX;
}

function getNudgeState(userId: number | undefined): MatchNudgeState {
  const fallback: MatchNudgeState = { showCount: 0, matchesSinceLastShow: 0 };
  try {
    const raw = localStorage.getItem(getNudgeStateKey(userId));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return {
      showCount: typeof parsed.showCount === 'number' ? parsed.showCount : 0,
      matchesSinceLastShow: typeof parsed.matchesSinceLastShow === 'number' ? parsed.matchesSinceLastShow : 0,
    };
  } catch {
    return fallback;
  }
}

function saveNudgeState(userId: number | undefined, state: MatchNudgeState) {
  localStorage.setItem(getNudgeStateKey(userId), JSON.stringify(state));
}

function shouldShowMatchInstallPrompt(userId: number | undefined): boolean {
  if (isStandalone() || isInstalled()) return false;
  if (!localStorage.getItem(ONBOARDING_INSTALL_SHOWN_KEY)) return false;
  if (!shouldShowInstallNudge()) return false;

  const state = getNudgeState(userId);
  if (state.showCount >= MAX_TOTAL_SHOWS) return false;

  if (state.showCount === 0) return true;

  if (state.matchesSinceLastShow >= MATCH_COOLDOWN) return true;

  return false;
}

function recordMatchForNudge(userId: number | undefined) {
  const state = getNudgeState(userId);
  if (state.showCount >= MAX_TOTAL_SHOWS) return;
  state.matchesSinceLastShow += 1;
  saveNudgeState(userId, state);
}

function recordNudgeShown(userId: number | undefined) {
  const state = getNudgeState(userId);
  state.showCount += 1;
  state.matchesSinceLastShow = 0;
  saveNudgeState(userId, state);
}

interface MatchInstallNudgeProps {
  bannerActive?: boolean;
}

export function MatchInstallNudge({ bannerActive = false }: MatchInstallNudgeProps) {
  const { user } = useAuth();
  const [location] = useLocation();
  const isSuppressedPath = SUPPRESSED_PATHS.some(p => location === p || location.startsWith(p + '/'));
  const [visible, setVisible] = useState(false);
  const [pendingShow, setPendingShow] = useState(false);
  const [matchName, setMatchName] = useState('');
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const platform = detectPlatform();

  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    return onGlobalMatch((data: MatchNotification) => {
      recordMatchForNudge(user?.id);

      if (isSuppressedPath) return;
      if (!shouldShowMatchInstallPrompt(user?.id)) return;
      setMatchName(data.matchedWith?.name || '');
      setPendingShow(true);
    });
  }, [user?.id, isSuppressedPath]);

  useEffect(() => {
    if (!pendingShow || bannerActive) return;
    const timer = setTimeout(() => {
      if (!shouldShowMatchInstallPrompt(user?.id)) {
        setPendingShow(false);
        return;
      }
      const state = getNudgeState(user?.id);
      recordNudgeShown(user?.id);
      setVisible(true);
      setPendingShow(false);
      track('pwa_install_screen_shown', {
        platform,
        trigger: TRIGGER_ID,
        show_number: state.showCount + 1,
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [pendingShow, bannerActive, platform, user?.id]);

  const handleInstall = useCallback(async () => {
    recordInstallTrigger(TRIGGER_ID);
    const device = platform.startsWith('android') ? 'android' : platform.startsWith('ios') ? 'ios' : 'desktop';

    if (deferredPrompt && platform.startsWith('android')) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        dismissInstallTrigger(TRIGGER_ID);
        track('pwa_install_accepted', { trigger: TRIGGER_ID, device });
      } else {
        track('pwa_install_dismissed', { trigger: TRIGGER_ID, device });
      }
      setVisible(false);
      return;
    }

    track('pwa_install_tapped', { trigger: TRIGGER_ID, device });
    setShowWalkthrough(true);
  }, [deferredPrompt, platform]);

  const handleDismiss = useCallback(() => {
    track('pwa_install_dismissed', { trigger: TRIGGER_ID });
    setVisible(false);
  }, []);

  if (isSuppressedPath) return null;

  if (showWalkthrough) {
    return (
      <InstallWalkthrough
        onClose={() => {
          setShowWalkthrough(false);
          handleDismiss();
        }}
        onDone={() => {
          setShowWalkthrough(false);
          dismissInstallTrigger(TRIGGER_ID);
          setVisible(false);
        }}
      />
    );
  }

  if (!visible) return null;

  const subtitle = matchName
    ? `Install Papacito to get notified when ${matchName} replies!`
    : 'Install Papacito to get notified when they reply!';

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 28 }}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9000,
            background: 'linear-gradient(180deg, #1a0a2e, #0d0018)',
            borderRadius: '20px 20px 0 0',
            padding: '20px 24px 44px',
            border: '1px solid rgba(255,255,255,0.1)',
            borderBottom: 'none',
          }}
        >
          <div
            style={{
              width: 36,
              height: 4,
              borderRadius: 2,
              background: 'rgba(255,255,255,0.15)',
              margin: '0 auto 20px',
            }}
          />

          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>🔥</div>
            <h3
              style={{
                color: '#fff',
                fontSize: 22,
                fontWeight: 800,
                margin: '0 0 10px',
                lineHeight: 1.2,
              }}
            >
              You got a match!
            </h3>
            <p
              style={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: 15,
                margin: '0 0 28px',
                lineHeight: 1.6,
              }}
            >
              {subtitle}
            </p>
            <button
              onClick={handleInstall}
              style={{
                width: '100%',
                background: '#e11d48',
                color: '#fff',
                border: 'none',
                borderRadius: 14,
                padding: '16px 0',
                fontSize: 17,
                fontWeight: 700,
                cursor: 'pointer',
                marginBottom: 12,
                boxShadow: '0 4px 20px rgba(225,29,72,0.4)',
              }}
            >
              📲 Install Papacito
            </button>
            <button
              onClick={handleDismiss}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.3)',
                fontSize: 14,
                cursor: 'pointer',
                padding: 8,
              }}
            >
              Not now
            </button>
          </div>
        </motion.div>
      </AnimatePresence>

      <div
        onClick={handleDismiss}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 8999,
        }}
      />
    </>
  );
}
